import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Mail, 
  Send, 
  TestTube, 
  FileText, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Settings,
  BarChart3,
  ClipboardList,
  Package,
  Calendar,
  Users,
  MapPin,
  Filter,
  Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import api from '../services/api';

const EmailPage = () => {
  const [emailStatus, setEmailStatus] = useState({ configured: false, sender_email: '', provider: '' });
  const [loading, setLoading] = useState(true);
  
  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Custom email
  const [customEmail, setCustomEmail] = useState({
    recipient_email: '',
    subject: '',
    message: '',
    type: 'general'
  });
  const [sendingCustom, setSendingCustom] = useState(false);
  
  // Advanced Report
  const [reportConfig, setReportConfig] = useState({
    recipient_email: '',
    report_title: 'Reporte Personalizado',
    include_summary: true,
    include_inventory: true,
    include_movements: false,
    include_audit: false,
    include_users: false,
    include_responsibles: false,
    // Inventory filters
    inventory_statuses: ['disponible', 'asignado', 'mantenimiento', 'falla', 'perdido', 'baja'],
    inventory_show_details: true,
    // Movements filters
    movements_date_from: '',
    movements_date_to: '',
    movements_type: 'all', // all, salida, retorno
    movements_limit: 20,
    // Audit filters
    audit_limit: 15,
    audit_actions: ['CREATE', 'UPDATE', 'DELETE'],
    // Custom message
    custom_intro: '',
    custom_footer: ''
  });
  const [sendingReport, setSendingReport] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Data for filters
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    loadEmailStatus();
    loadFilterData();
  }, []);

  const loadEmailStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email/status');
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Error loading email status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterData = async () => {
    try {
      const [catRes, locRes] = await Promise.all([
        api.get('/categories'),
        api.get('/locations')
      ]);
      setCategories(catRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Ingresa un correo electrónico');
      return;
    }
    
    try {
      setSendingTest(true);
      await api.post('/email/test', { recipient_email: testEmail });
      toast.success(`Correo de prueba enviado a ${testEmail}`);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo de prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendCustomEmail = async () => {
    if (!customEmail.recipient_email || !customEmail.subject || !customEmail.message) {
      toast.error('Completa todos los campos');
      return;
    }
    
    try {
      setSendingCustom(true);
      await api.post('/email/send-custom', customEmail);
      toast.success(`Correo enviado a ${customEmail.recipient_email}`);
      setCustomEmail({ recipient_email: '', subject: '', message: '', type: 'general' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo');
    } finally {
      setSendingCustom(false);
    }
  };

  const handleSendAdvancedReport = async () => {
    if (!reportConfig.recipient_email) {
      toast.error('Ingresa un correo electrónico');
      return;
    }
    
    const hasContent = reportConfig.include_inventory || reportConfig.include_movements || 
                       reportConfig.include_audit || reportConfig.include_users || 
                       reportConfig.include_responsibles || reportConfig.include_summary;
    
    if (!hasContent) {
      toast.error('Selecciona al menos una sección para incluir en el reporte');
      return;
    }
    
    try {
      setSendingReport(true);
      await api.post('/email/send-advanced-report', reportConfig);
      toast.success(`Reporte enviado a ${reportConfig.recipient_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar reporte');
    } finally {
      setSendingReport(false);
    }
  };

  const toggleInventoryStatus = (status) => {
    setReportConfig(prev => ({
      ...prev,
      inventory_statuses: prev.inventory_statuses.includes(status)
        ? prev.inventory_statuses.filter(s => s !== status)
        : [...prev.inventory_statuses, status]
    }));
  };

  const toggleAuditAction = (action) => {
    setReportConfig(prev => ({
      ...prev,
      audit_actions: prev.audit_actions.includes(action)
        ? prev.audit_actions.filter(a => a !== action)
        : [...prev.audit_actions, action]
    }));
  };

  const inventoryStatuses = [
    { value: 'disponible', label: 'Disponible', color: 'bg-green-500' },
    { value: 'asignado', label: 'Asignado', color: 'bg-blue-500' },
    { value: 'mantenimiento', label: 'Mantenimiento', color: 'bg-yellow-500' },
    { value: 'falla', label: 'Con Falla', color: 'bg-red-500' },
    { value: 'perdido', label: 'Perdido', color: 'bg-purple-500' },
    { value: 'baja', label: 'Dado de Baja', color: 'bg-gray-500' },
  ];

  const auditActions = [
    { value: 'CREATE', label: 'Creaciones' },
    { value: 'UPDATE', label: 'Actualizaciones' },
    { value: 'DELETE', label: 'Eliminaciones' },
    { value: 'LOGIN', label: 'Inicios de Sesión' },
  ];

  const emailTypes = [
    { value: 'general', label: 'General', icon: '📧' },
    { value: 'notification', label: 'Notificación', icon: '🔔' },
    { value: 'alert', label: 'Alerta', icon: '⚠️' },
    { value: 'report', label: 'Reporte', icon: '📊' },
  ];

  // Count selected sections
  const selectedSections = [
    reportConfig.include_summary,
    reportConfig.include_inventory,
    reportConfig.include_movements,
    reportConfig.include_audit,
    reportConfig.include_users,
    reportConfig.include_responsibles
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Mail className="h-8 w-8 text-primary" />
          Gestión de Correos
        </h1>
        <p className="text-muted-foreground mt-1">
          Envía correos personalizados, reportes y prueba la configuración
        </p>
      </div>

      {/* Email Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Estado del Servicio de Correo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : emailStatus.configured ? (
            <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="font-medium text-green-600">Servicio de correo configurado</p>
                <p className="text-sm text-muted-foreground">
                  Proveedor: <strong>{emailStatus.provider}</strong> | 
                  Remitente: <strong>{emailStatus.sender_email}</strong>
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertCircle className="h-8 w-8 text-amber-500" />
              <div>
                <p className="font-medium text-amber-600">Servicio de correo no configurado</p>
                <p className="text-sm text-muted-foreground">
                  Configura las variables MS365 en Railway.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="test" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="test" className="flex items-center gap-2">
            <TestTube className="h-4 w-4" />
            Probar Correo
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Correo Personalizado
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Enviar Reportes
          </TabsTrigger>
        </TabsList>

        {/* Test Email Tab */}
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Probar Configuración de Correo
              </CardTitle>
              <CardDescription>
                Envía un correo de prueba para verificar que la configuración funciona
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  Se enviará un correo de prueba con el diseño de CM Idiomas.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="testEmail">Correo del destinatario</Label>
                <div className="flex gap-2">
                  <Input
                    id="testEmail"
                    type="email"
                    placeholder="ejemplo@correo.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleSendTestEmail}
                    disabled={sendingTest || !emailStatus.configured}
                  >
                    {sendingTest ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</>
                    ) : (
                      <><Send className="mr-2 h-4 w-4" />Enviar Prueba</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Email Tab */}
        <TabsContent value="custom">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Enviar Correo Personalizado
              </CardTitle>
              <CardDescription>
                Redacta y envía un correo con el diseño de CM Idiomas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customRecipient">Destinatario</Label>
                  <Input
                    id="customRecipient"
                    type="email"
                    placeholder="ejemplo@correo.com"
                    value={customEmail.recipient_email}
                    onChange={(e) => setCustomEmail({...customEmail, recipient_email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de correo</Label>
                  <Select 
                    value={customEmail.type}
                    onValueChange={(v) => setCustomEmail({...customEmail, type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <span>{type.icon}</span>{type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customSubject">Asunto</Label>
                <Input
                  id="customSubject"
                  placeholder="Asunto del correo"
                  value={customEmail.subject}
                  onChange={(e) => setCustomEmail({...customEmail, subject: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="customMessage">Mensaje</Label>
                <Textarea
                  id="customMessage"
                  placeholder="Escribe tu mensaje aquí..."
                  value={customEmail.message}
                  onChange={(e) => setCustomEmail({...customEmail, message: e.target.value})}
                  rows={6}
                />
              </div>
              
              <Button 
                onClick={handleSendCustomEmail}
                disabled={sendingCustom || !emailStatus.configured}
                className="w-full"
              >
                {sendingCustom ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" />Enviar Correo</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced Reports Tab */}
        <TabsContent value="reports">
          <div className="space-y-4">
            {/* Report Header */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Configurar Reporte Personalizado
                </CardTitle>
                <CardDescription>
                  Selecciona qué información incluir en tu reporte y personaliza los filtros
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Correo del destinatario</Label>
                    <Input
                      type="email"
                      placeholder="ejemplo@correo.com"
                      value={reportConfig.recipient_email}
                      onChange={(e) => setReportConfig({...reportConfig, recipient_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Título del reporte</Label>
                    <Input
                      placeholder="Reporte Personalizado"
                      value={reportConfig.report_title}
                      onChange={(e) => setReportConfig({...reportConfig, report_title: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Mensaje introductorio (opcional)</Label>
                  <Textarea
                    placeholder="Escribe un mensaje personalizado para incluir al inicio del reporte..."
                    value={reportConfig.custom_intro}
                    onChange={(e) => setReportConfig({...reportConfig, custom_intro: e.target.value})}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Sections Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Secciones del Reporte
                  <span className="ml-auto text-sm font-normal text-muted-foreground">
                    {selectedSections} seleccionadas
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {/* Summary */}
                  <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    reportConfig.include_summary ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                  }`}>
                    <Checkbox
                      checked={reportConfig.include_summary}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, include_summary: c})}
                    />
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <BarChart3 className="h-4 w-4" /> Resumen General
                      </div>
                      <p className="text-xs text-muted-foreground">KPIs y estadísticas</p>
                    </div>
                  </label>

                  {/* Inventory */}
                  <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    reportConfig.include_inventory ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                  }`}>
                    <Checkbox
                      checked={reportConfig.include_inventory}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, include_inventory: c})}
                    />
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <Package className="h-4 w-4" /> Inventario
                      </div>
                      <p className="text-xs text-muted-foreground">Estado de equipos</p>
                    </div>
                  </label>

                  {/* Movements */}
                  <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    reportConfig.include_movements ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                  }`}>
                    <Checkbox
                      checked={reportConfig.include_movements}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, include_movements: c})}
                    />
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <ClipboardList className="h-4 w-4" /> Movimientos
                      </div>
                      <p className="text-xs text-muted-foreground">Salidas y retornos</p>
                    </div>
                  </label>

                  {/* Audit */}
                  <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    reportConfig.include_audit ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                  }`}>
                    <Checkbox
                      checked={reportConfig.include_audit}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, include_audit: c})}
                    />
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <FileText className="h-4 w-4" /> Auditoría
                      </div>
                      <p className="text-xs text-muted-foreground">Registro de actividad</p>
                    </div>
                  </label>

                  {/* Users */}
                  <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    reportConfig.include_users ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                  }`}>
                    <Checkbox
                      checked={reportConfig.include_users}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, include_users: c})}
                    />
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <Users className="h-4 w-4" /> Usuarios
                      </div>
                      <p className="text-xs text-muted-foreground">Lista de usuarios</p>
                    </div>
                  </label>

                  {/* Responsibles */}
                  <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    reportConfig.include_responsibles ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                  }`}>
                    <Checkbox
                      checked={reportConfig.include_responsibles}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, include_responsibles: c})}
                    />
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <Users className="h-4 w-4" /> Responsables
                      </div>
                      <p className="text-xs text-muted-foreground">Lista de responsables</p>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Inventory Filters */}
            {reportConfig.include_inventory && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Package className="h-4 w-4" /> Filtros de Inventario
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Estados a incluir</Label>
                    <div className="flex flex-wrap gap-2">
                      {inventoryStatuses.map(status => (
                        <label
                          key={status.value}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-sm ${
                            reportConfig.inventory_statuses.includes(status.value)
                              ? 'bg-primary/10 border-primary'
                              : 'bg-muted/30 hover:bg-muted/50'
                          }`}
                        >
                          <Checkbox
                            checked={reportConfig.inventory_statuses.includes(status.value)}
                            onCheckedChange={() => toggleInventoryStatus(status.value)}
                            className="h-3.5 w-3.5"
                          />
                          <span className={`w-2 h-2 rounded-full ${status.color}`}></span>
                          {status.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="showDetails"
                      checked={reportConfig.inventory_show_details}
                      onCheckedChange={(c) => setReportConfig({...reportConfig, inventory_show_details: c})}
                    />
                    <label htmlFor="showDetails" className="text-sm cursor-pointer">
                      Mostrar detalle por categoría y ubicación
                    </label>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Movements Filters */}
            {reportConfig.include_movements && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ClipboardList className="h-4 w-4" /> Filtros de Movimientos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de movimiento</Label>
                      <Select
                        value={reportConfig.movements_type}
                        onValueChange={(v) => setReportConfig({...reportConfig, movements_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="salida">Solo Salidas</SelectItem>
                          <SelectItem value="retorno">Solo Retornos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha desde</Label>
                      <Input
                        type="date"
                        value={reportConfig.movements_date_from}
                        onChange={(e) => setReportConfig({...reportConfig, movements_date_from: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha hasta</Label>
                      <Input
                        type="date"
                        value={reportConfig.movements_date_to}
                        onChange={(e) => setReportConfig({...reportConfig, movements_date_to: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad máxima de movimientos</Label>
                    <Select
                      value={reportConfig.movements_limit.toString()}
                      onValueChange={(v) => setReportConfig({...reportConfig, movements_limit: parseInt(v)})}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">Últimos 10</SelectItem>
                        <SelectItem value="20">Últimos 20</SelectItem>
                        <SelectItem value="50">Últimos 50</SelectItem>
                        <SelectItem value="100">Últimos 100</SelectItem>
                        <SelectItem value="999">Todos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Audit Filters */}
            {reportConfig.include_audit && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-4 w-4" /> Filtros de Auditoría
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tipos de acción a incluir</Label>
                    <div className="flex flex-wrap gap-2">
                      {auditActions.map(action => (
                        <label
                          key={action.value}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-sm ${
                            reportConfig.audit_actions.includes(action.value)
                              ? 'bg-primary/10 border-primary'
                              : 'bg-muted/30 hover:bg-muted/50'
                          }`}
                        >
                          <Checkbox
                            checked={reportConfig.audit_actions.includes(action.value)}
                            onCheckedChange={() => toggleAuditAction(action.value)}
                            className="h-3.5 w-3.5"
                          />
                          {action.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad de registros</Label>
                    <Select
                      value={reportConfig.audit_limit.toString()}
                      onValueChange={(v) => setReportConfig({...reportConfig, audit_limit: parseInt(v)})}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">Últimos 10</SelectItem>
                        <SelectItem value="15">Últimos 15</SelectItem>
                        <SelectItem value="25">Últimos 25</SelectItem>
                        <SelectItem value="50">Últimos 50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Footer Message */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <Label>Mensaje de cierre (opcional)</Label>
                  <Textarea
                    placeholder="Escribe un mensaje para incluir al final del reporte..."
                    value={reportConfig.custom_footer}
                    onChange={(e) => setReportConfig({...reportConfig, custom_footer: e.target.value})}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Send Button */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPreview(true)}
                className="flex-1"
              >
                <Eye className="mr-2 h-4 w-4" />
                Vista Previa
              </Button>
              <Button 
                onClick={handleSendAdvancedReport}
                disabled={sendingReport || !emailStatus.configured || selectedSections === 0}
                className="flex-1"
              >
                {sendingReport ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generando y enviando...</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" />Enviar Reporte ({selectedSections} secciones)</>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vista Previa del Reporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="bg-muted/50 rounded-lg p-4">
              <p><strong>Destinatario:</strong> {reportConfig.recipient_email || '(sin especificar)'}</p>
              <p><strong>Título:</strong> {reportConfig.report_title}</p>
            </div>
            
            {reportConfig.custom_intro && (
              <div className="border-l-4 border-primary pl-4">
                <p className="text-muted-foreground">{reportConfig.custom_intro}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <p className="font-medium">Secciones incluidas:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {reportConfig.include_summary && <li>Resumen General (KPIs)</li>}
                {reportConfig.include_inventory && (
                  <li>Inventario - Estados: {reportConfig.inventory_statuses.join(', ')}</li>
                )}
                {reportConfig.include_movements && (
                  <li>Movimientos - Tipo: {reportConfig.movements_type}, Límite: {reportConfig.movements_limit}</li>
                )}
                {reportConfig.include_audit && (
                  <li>Auditoría - Acciones: {reportConfig.audit_actions.join(', ')}, Límite: {reportConfig.audit_limit}</li>
                )}
                {reportConfig.include_users && <li>Lista de Usuarios</li>}
                {reportConfig.include_responsibles && <li>Lista de Responsables</li>}
              </ul>
            </div>
            
            {reportConfig.custom_footer && (
              <div className="border-l-4 border-muted pl-4">
                <p className="text-muted-foreground">{reportConfig.custom_footer}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailPage;
